/**
 * ContentPicker — Embeddable iframe integration for Content Library provider.
 *
 * When CONTENT_LIBRARY_PROVIDER_MODE is enabled (via REACT_APP_CONTENT_LIBRARY_PROVIDER_MODE),
 * this component renders an iframe pointing to the Content Library's /embed/picker route.
 * The iframe communicates via postMessage to return selected asset data.
 *
 * When provider mode is disabled, it falls back to the original ContentPicker
 * which queries the runtime's /api/content-library/picker/assets endpoint directly.
 *
 * postMessage Protocol (standardized — Issue #15):
 *   1. CL iframe sends: { source:'nurturecrm', type:'nurturecrm:ready', iframeId:'content-picker' }
 *   2. CRM sends: { source:'nurturecrm', type:'nurturecrm:context', token:'<jwt>' }
 *   3. User selects asset in CL iframe
 *   4. CL iframe sends: { source:'nurturecrm', type:'nurturecrm:asset-selected', payload:{assetId,name,fileType,proxyUrl,externalUrl,externalProvider} }
 *   5. CRM receives, normalizes, and calls onSelect callback
 *   Alternative: CL iframe sends { source:'nurturecrm', type:'nurturecrm:cancel' }
 *
 * Asset shape from CL iframe (in payload):
 *   { assetId, name, fileType, proxyUrl, externalUrl, externalProvider }
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
    FolderOpen, Search, Image, FileText, Video, ChevronRight, ChevronDown,
    Loader2, X, Check
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature flag from env
const PROVIDER_MODE = process.env.REACT_APP_CONTENT_LIBRARY_PROVIDER_MODE === 'true';
const CL_URL = process.env.REACT_APP_CONTENT_LIBRARY_URL || 'https://content.nurturecrm.local';

function getFileIcon(type) {
    switch (type) {
        case 'image': return Image;
        case 'video': return Video;
        default: return FileText;
    }
}

function getTypeColor(type) {
    switch (type) {
        case 'image': return 'text-blue-600 bg-blue-50';
        case 'video': return 'text-purple-600 bg-purple-50';
        case 'document': return 'text-amber-600 bg-amber-50';
        default: return 'text-stone-600 bg-stone-50';
    }
}

function formatFileSize(bytes) {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getVideoEmbedUrl(url, provider) {
    if (!url) return null;
    if (provider === 'youtube') {
        const match = url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
        return match ? `https://www.youtube.com/embed/${match[1]}` : null;
    }
    if (provider === 'loom') {
        const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
        return match ? `https://www.loom.com/embed/${match[1]}` : null;
    }
    if (provider === 'vimeo') {
        const match = url.match(/vimeo\.com\/(\d+)/);
        return match ? `https://player.vimeo.com/video/${match[1]}` : null;
    }
    return null;
}

// ─── Provider Mode: Iframe Embed ─────────────────────────────────────────────

function ContentPickerIframe({ open, onSelect, onOpenChange, fileType, allowedTypes, multiSelect = false }) {
    const iframeRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);

    // The CRM runtime must generate a context JWT and pass it to the iframe.
    // For now, we use the auth token from the AuthContext as a placeholder.
    // TODO: The CRM runtime backend needs an endpoint to generate context JWTs
    // signed with the tenant shared secret. This is a NurtureCRM-side prerequisite.
    const { user } = useAuth();
    const [contextJwt, setContextJwt] = useState(null);

    // Request context JWT from CRM runtime backend
    useEffect(() => {
        // The CRM runtime should provide an endpoint to generate a context JWT
        // for the current tenant/user. This is a NurtureCRM-side issue.
        // For now, we skip this and let the iframe handle auth.
        // When the CRM runtime provides POST /api/content-library/context-jwt,
        // this effect will call it and setContextJwt.
    }, []);

    // Derive the CL origin from CL_URL for postMessage origin validation
    const CL_ORIGIN = (() => {
        try { return new URL(CL_URL).origin; }
        catch { return '*'; }
    })();

    useEffect(() => {
        const handleMessage = (event) => {
            // Security: validate origin against CL_URL origin
            if (CL_ORIGIN !== '*' && event.origin !== CL_ORIGIN) return;

            const msg = event.data;
            // Security: reject messages without source: 'nurturecrm'
            if (msg.source !== 'nurturecrm') return;

            if (msg.type === 'nurturecrm:ready') {
                setReady(true);
                // Send context JWT to the iframe
                if (contextJwt && iframeRef.current) {
                    iframeRef.current.contentWindow.postMessage({
                        source: 'nurturecrm',
                        type: 'nurturecrm:context',
                        token: contextJwt,
                    }, CL_ORIGIN);
                }
            } else if (msg.type === 'nurturecrm:asset-selected') {
                // Normalize CL asset shape to match runtime expectations
                const payload = msg.payload;
                const normalized = {
                    asset_id: payload.assetId,
                    name: payload.name,
                    file_type: payload.fileType,
                    proxy_url: payload.proxyUrl,
                    external_url: payload.externalUrl || null,
                    external_provider: payload.externalProvider || null,
                    url: payload.proxyUrl, // Use proxy URL for compatibility
                };

                if (multiSelect) {
                    onSelect([normalized]);
                } else {
                    onSelect(normalized);
                }
                onOpenChange?.(false);
            } else if (msg.type === 'nurturecrm:cancel') {
                onOpenChange?.(false);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [onSelect, onOpenChange, multiSelect, contextJwt, CL_ORIGIN]);

    // Build embed URL with standardized query params
    const fileTypesParam = allowedTypes ? allowedTypes.join(',') : (fileType && fileType !== 'all' ? fileType : '');
    const embedUrl = `${CL_URL}/embed/picker?embed=true${fileTypesParam ? `&file_types=${fileTypesParam}` : ''}`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl h-[80vh]">
            <DialogHeader>
                <DialogTitle>Content Library — Select Asset</DialogTitle>
                <DialogDescription>
                    Browse and select assets from your Content Library
                </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-hidden rounded-lg border border-stone-200" style={{ height: 'calc(80vh - 120px)' }}>
                {!ready && (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
                        <span className="ml-2 text-stone-500">Loading Content Library…</span>
                    </div>
                )}
                {error && (
                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                        <p className="text-red-600 font-medium mb-2">Failed to load Content Library</p>
                        <p className="text-sm text-stone-500 mb-4">{error}</p>
                        <Button variant="outline" onClick={() => window.location.reload()}>
                            Retry
                        </Button>
                    </div>
                )}
                <iframe
                    ref={iframeRef}
                    src={embedUrl}
                    style={{ width: '100%', height: '100%', border: 'none', display: ready ? 'block' : 'none' }}
                    title="Content Library Picker"
                    allow="clipboard-read; clipboard-write"
                />
            </div>
        </DialogContent>
        </Dialog>
    );
}

// ─── Legacy Mode: Direct API ─────────────────────────────────────────────────

function ContentPickerLegacy({ open, onSelect, onClose, onOpenChange, fileType, multiSelect = false }) {
    const { user } = useAuth();
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(multiSelect ? [] : null);
    const [currentFolder, setCurrentFolder] = useState(null);

    const loadAssets = useCallback(async () => {
        setLoading(true);
        try {
            const params = { file_type: fileType || 'all' };
            if (search) params.search = search;
            if (currentFolder) params.folder = currentFolder;
            const res = await axios.get(`${API}/content-library/picker/assets`, { params });
            setAssets(res.data || []);
        } catch (err) {
            console.error('Failed to load assets:', err);
        } finally {
            setLoading(false);
        }
    }, [fileType, search, currentFolder]);

    useEffect(() => {
        loadAssets();
    }, [loadAssets]);

    const handleSelect = (asset) => {
        if (multiSelect) {
            setSelected(prev => {
                const exists = prev.find(a => a.asset_id === asset.asset_id);
                if (exists) return prev.filter(a => a.asset_id !== asset.asset_id);
                return [...prev, asset];
            });
        } else {
            setSelected(asset);
        }
    };

    const handleConfirm = () => {
        if (selected) {
            onSelect(selected);
            onClose();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange || onClose}>
        <DialogContent className="max-w-5xl h-[80vh]">
            <DialogHeader>
                <DialogTitle>Select Content</DialogTitle>
                <DialogDescription>Choose from your content library</DialogDescription>
            </DialogHeader>
            <div className="flex gap-4 mb-4">
                <Input
                    placeholder="Search assets…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="flex-1"
                />
                <Button variant="outline" onClick={loadAssets}>Refresh</Button>
            </div>
            <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 200px)' }}>
                {loading ? (
                    <div className="flex justify-center p-8">
                        <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
                    </div>
                ) : assets.length === 0 ? (
                    <div className="text-center p-8 text-stone-500">No assets found</div>
                ) : (
                    <div className="grid grid-cols-4 gap-3">
                        {assets.map(asset => {
                            const Icon = getFileIcon(asset.file_type);
                            const isSelected = multiSelect
                                ? selected?.find(a => a.asset_id === asset.asset_id)
                                : selected?.asset_id === asset.asset_id;
                            return (
                                <div
                                    key={asset.asset_id}
                                    onClick={() => handleSelect(asset)}
                                    className={`relative cursor-pointer rounded-lg border-2 p-3 transition-all ${
                                        isSelected ? 'border-blue-500 bg-blue-50' : 'border-stone-200 hover:border-stone-300'
                                    }`}
                                >
                                    {isSelected && (
                                        <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-1">
                                            <Check className="h-3 w-3 text-white" />
                                        </div>
                                    )}
                                    <Icon className="h-8 w-8 mx-auto mb-2 text-stone-400" />
                                    <p className="text-xs text-center truncate">{asset.name}</p>
                                    <p className="text-xs text-center text-stone-400">{formatFileSize(asset.file_size)}</p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={handleConfirm} disabled={!selected}>Select</Button>
            </div>
        </DialogContent>
        </Dialog>
    );
}

// Exported for use by RichTextEditor/SignatureEditor to generate embed HTML
export { getVideoEmbedUrl };

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function ContentPicker(props) {
    if (PROVIDER_MODE) {
        return <ContentPickerIframe {...props} />;
    }
    return <ContentPickerLegacy {...props} />;
}
